/**
 * Paying an order from a connected US bank account (ACH Direct Debit) is a
 * MyCanvas PRO feature with a 5% merchandise discount. The backend decides
 * eligibility and reports it on /users/me as two flags: `ach_available` (the
 * brand offers ACH and the account is PRO) and `ach_eligible` (that plus an
 * active bank account). The checkout offers the bank option on those flags
 * alone. The pieces of that flow that are plain data live here, free of
 * React, so their edges are testable.
 */

export const ACH_PAYMENT_METHOD = 'ach';

// How long the bank usually takes to answer; Stripe settles ACH in up to
// four business days.
export const ACH_SETTLEMENT_NOTE =
  'Printing starts once your bank confirms the payment, usually within four business days.';

// Can the bank be charged? `ach_eligible` already folds in the brand flag,
// PRO and an active account; a blocked or revoked account is not eligible.
export const achPayable = (user) => Boolean(user?.ach_eligible && user?.bank_account?.active);

// Is the bank tab worth showing so an account can be connected inline? The
// brand offers ACH to this PRO but there is no active account: none yet,
// blocked or revoked. The SetupIntent endpoint is guarded on PRO only.
export const achConnectable = (user) => Boolean(user?.ach_available && !user?.bank_account?.active);

// The tab the checkout opens on. An explicit pick wins while it is still
// offered; otherwise the bank when it can be charged, because the discount
// is the reason the account was connected; otherwise the first type (card).
// A merely connectable bank has to be opted into.
export const checkoutPaymentType = (chosen, paymentTypes, bankPayable) => {
  if (chosen && paymentTypes.includes(chosen)) return chosen;
  return bankPayable && paymentTypes.includes(ACH_PAYMENT_METHOD)
    ? ACH_PAYMENT_METHOD
    : paymentTypes[0];
};

// Should the bank summary take focus? Connecting inline ends with the user
// reload swapping the connect form for the summary, and the form's own
// focus handling dies with it. The summary takes over exactly when the
// account turns active while the bank tab is open: not on mount with an
// already active account, not on a tab switch, not on a reload that changes
// nothing.
export const focusBankSummary = (wasActive, isActive, bankTabSelected) =>
  Boolean(!wasActive && isActive && bankTabSelected);

// `payment_data.ach` for POST /orders. The id must be the connected account:
// the backend refuses any other rather than charging the right one, because a
// mismatch means the client's idea of the account is stale.
export const achPaymentData = (bankAccount) => ({
  ach: { bank_account_id: bankAccount.id },
});

// Account credit and the bank discount never combine: credit pays what it
// can and the discount is zero when any credit is on the account. The quote
// says so through the amounts; this turns it into a sentence for the summary.
export const achDiscountNotice = (priceData) => {
  if (!priceData || Number(priceData.ach_discount) > 0) {
    return null;
  }
  if (Number(priceData.credit_discount) > 0) {
    return 'Your account credit is applied first, so the bank payment discount does not apply to this order.';
  }
  return null;
};

// What a card is charged for a bank order whose debit was returned: the
// stored checkout total with the bank discount taken off again. Not a
// repricing, so the order's own payment_info carries both numbers.
export const repayAmount = (paymentInfo) => {
  const amount = Number(paymentInfo.overall) + Number(paymentInfo.ach_discount ?? 0);
  return { amount, amountInCents: Math.round(amount * 100) };
};
